/**
 * N8N Workflows Controller
 *
 * Manages state and gateway RPC calls for n8n workflow administration.
 */

import type { N8nAdminProps, N8nWorkflowEntry } from "../views/n8n-admin.ts";

// --------------- State ---------------

export interface N8nState {
  client: { request<T>(method: string, params: unknown): Promise<T> } | null;
  connected: boolean;
  igProjects: Array<{ id: string; name: string; slug?: string }>;

  n8nWorkflows: N8nWorkflowEntry[];
  n8nLoading: boolean;
  n8nError: string | null;
  n8nCreateMode: boolean;
  n8nEditingId: string | null;
  n8nForm: Partial<N8nWorkflowEntry> | null;
  n8nFilterProjectId: string | null;
}

export const N8N_STATE_DEFAULTS: Omit<N8nState, "client" | "connected" | "igProjects"> = {
  n8nWorkflows: [],
  n8nLoading: false,
  n8nError: null,
  n8nCreateMode: false,
  n8nEditingId: null,
  n8nForm: null,
  n8nFilterProjectId: null,
};

// --------------- Helpers ---------------

function friendlyError(err: unknown): string {
  const msg = String(err).toLowerCase();
  if (msg.includes("method not found") || msg.includes("not registered")) {
    return "N8N methods not available. Ensure the igloobot-core plugin is loaded.";
  }
  return String(err);
}

// --------------- CRUD ---------------

type ListResult = { ok: boolean; data?: N8nWorkflowEntry[]; error?: string };
type ItemResult = { ok: boolean; data?: N8nWorkflowEntry; error?: string };
type SimpleResult = { ok: boolean; error?: string };

export async function loadN8nWorkflows(state: N8nState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.n8nLoading = true;
  state.n8nError = null;
  try {
    const params: Record<string, unknown> = {};
    if (state.n8nFilterProjectId) {
      params.projectId = state.n8nFilterProjectId;
    }
    const res = await state.client.request<ListResult>("igloobot.n8n.list", params);
    if (res.ok && res.data) {
      state.n8nWorkflows = res.data;
    } else {
      state.n8nError = res.error ?? "Failed to load n8n workflows";
    }
  } catch (err) {
    state.n8nError = friendlyError(err);
  } finally {
    state.n8nLoading = false;
  }
}

export async function createN8nWorkflow(state: N8nState): Promise<void> {
  if (!state.client || !state.connected || !state.n8nForm) {
    return;
  }
  state.n8nLoading = true;
  try {
    const res = await state.client.request<ItemResult>("igloobot.n8n.create", state.n8nForm);
    if (res.ok) {
      state.n8nCreateMode = false;
      state.n8nForm = null;
      await loadN8nWorkflows(state);
    } else {
      state.n8nError = res.error ?? "Failed to create workflow";
    }
  } catch (err) {
    state.n8nError = friendlyError(err);
  } finally {
    state.n8nLoading = false;
  }
}

export async function updateN8nWorkflow(state: N8nState): Promise<void> {
  if (!state.client || !state.connected || !state.n8nEditingId || !state.n8nForm) {
    return;
  }
  state.n8nLoading = true;
  try {
    const res = await state.client.request<ItemResult>("igloobot.n8n.update", {
      id: state.n8nEditingId,
      ...state.n8nForm,
    });
    if (res.ok) {
      state.n8nEditingId = null;
      state.n8nForm = null;
      await loadN8nWorkflows(state);
    } else {
      state.n8nError = res.error ?? "Failed to update workflow";
    }
  } catch (err) {
    state.n8nError = friendlyError(err);
  } finally {
    state.n8nLoading = false;
  }
}

export async function deleteN8nWorkflow(state: N8nState, id: string): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<SimpleResult>("igloobot.n8n.delete", { id });
    if (res.ok) {
      await loadN8nWorkflows(state);
    } else {
      state.n8nError = res.error ?? "Failed to delete workflow";
    }
  } catch (err) {
    state.n8nError = friendlyError(err);
  }
}

export async function toggleN8nWorkflow(
  state: N8nState,
  id: string,
  isActive: boolean,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request<ItemResult>("igloobot.n8n.update", { id, isActive });
    await loadN8nWorkflows(state);
  } catch (err) {
    state.n8nError = friendlyError(err);
  }
}

// --------------- Form helpers ---------------

export function startCreateN8n(state: N8nState): void {
  state.n8nCreateMode = true;
  state.n8nEditingId = null;
  state.n8nForm = {
    projectId: state.igProjects[0]?.id ?? "",
    name: "",
    n8nWorkflowId: "",
    n8nBaseUrl: "http://localhost:5678",
    n8nApiKey: "",
    description: "",
    triggerType: "manual",
    inboundWebhookPath: "",
  };
}

export function startEditN8n(state: N8nState, wf: N8nWorkflowEntry): void {
  state.n8nCreateMode = false;
  state.n8nEditingId = wf.id;
  state.n8nForm = { ...wf };
}

export function cancelN8nForm(state: N8nState): void {
  state.n8nCreateMode = false;
  state.n8nEditingId = null;
  state.n8nForm = null;
}

export function updateN8nForm(state: N8nState, field: string, value: unknown): void {
  if (!state.n8nForm) {
    return;
  }
  state.n8nForm = { ...state.n8nForm, [field]: value };
}

// --------------- Props builder ---------------

export function buildN8nAdminProps(state: N8nState): N8nAdminProps {
  return {
    workflows: state.n8nWorkflows,
    loading: state.n8nLoading,
    error: state.n8nError,
    createMode: state.n8nCreateMode,
    editingId: state.n8nEditingId,
    form: state.n8nForm,
    filterProjectId: state.n8nFilterProjectId,
    projects: state.igProjects,
    onRefresh: () => loadN8nWorkflows(state),
    onCreate: () => startCreateN8n(state),
    onSave: () => {
      if (state.n8nEditingId) {
        void updateN8nWorkflow(state);
      } else {
        void createN8nWorkflow(state);
      }
    },
    onEdit: (wf) => startEditN8n(state, wf),
    onCancel: () => cancelN8nForm(state),
    onDelete: (id) => {
      if (confirm("Delete this n8n workflow registration? This cannot be undone.")) {
        void deleteN8nWorkflow(state, id);
      }
    },
    onToggle: (id, active) => toggleN8nWorkflow(state, id, active),
    onFormChange: (field, value) => updateN8nForm(state, field, value),
    onFilterChange: (projectId) => {
      state.n8nFilterProjectId = projectId;
      void loadN8nWorkflows(state);
    },
  };
}
